import clsx from 'clsx';
import Switch from 'components/atoms/Switch/Switch';
import Txt from 'components/atoms/Txt/Txt';
import React, { useState } from 'react';

import classes from './SurveyInvitations.module.scss';
import { graphql, useFragment, useMutation } from 'react-relay';
import { SurveyInvitationsMutation } from './__generated__/SurveyInvitationsMutation.graphql';

export type Props = {
  notificationsSubscriptionsRef: any;
  className?: string;
};

const notificationSubscriptionsFragment = graphql`
  fragment SurveyInvitations_notificationSubscriptions on NotificationSubscriptions {
    id
    surveyInviteContactChannels
  }
`;

const updateSurveyInviteSubscriptionsMutation = graphql`
  mutation SurveyInvitationsMutation($input: UpdateNotificationSubscriptions!) {
    updateNotificationContactChannelSubscriptions(input: $input) {
      message
      notificationSubscriptions {
        id
        surveyInviteContactChannels
      }
    }
  }
`;

export default function SurveyInvitations({ notificationsSubscriptionsRef, className }: Props) {
  const subscriptions = useFragment(
    notificationSubscriptionsFragment,
    notificationsSubscriptionsRef,
  );

  const [commitMutation, isMutationInFlight] = useMutation<SurveyInvitationsMutation>(
    updateSurveyInviteSubscriptionsMutation,
  );

  function updateDataLayer(selectedChannel: 'email' | 'sms' | 'pushNotification') {
    if (!subscriptionChannels?.surveyInviteContactChannels) return;

    const currentChannels = subscriptionChannels.surveyInviteContactChannels;
    const isCurrentlyEnabled = currentChannels.includes(selectedChannel);

    const updatedChannels = isCurrentlyEnabled
      ? currentChannels.filter((currentChannel) => currentChannel !== selectedChannel)
      : [...currentChannels, selectedChannel];

    setSubscriptionChannels({
      surveyInviteContactChannels: updatedChannels,
    });

    commitMutation({
      variables: {
        input: {
          surveyInviteContactChannels: updatedChannels,
        },
      },
      onCompleted: (response) => {
        const updated =
          response.updateNotificationContactChannelSubscriptions?.notificationSubscriptions
            ?.surveyInviteContactChannels;

        if (updated) {
          setSubscriptionChannels({ surveyInviteContactChannels: [...updated] });
        }
      },
      onError: (err) => {
        console.error('Survey subscription update error:', err);
      },
    });
  }

  const [subscriptionChannels, setSubscriptionChannels] = useState<{
    surveyInviteContactChannels: string[] | null;
  }>();

  React.useEffect(() => {
    setSubscriptionChannels({
      surveyInviteContactChannels: subscriptions.surveyInviteContactChannels,
    });
  }, [subscriptions]);

  return (
    <>
      <Txt component="p" className={classes.description}>
        Enable notifications when there are new surveys available, so you never miss a chance to
        earn.
      </Txt>
      <div className={clsx(className, classes.card)}>
        <Txt className={classes.label}>SMS</Txt>
        <Switch
          isLoading={isMutationInFlight || !subscriptions}
          checked={subscriptionChannels?.surveyInviteContactChannels?.includes('sms')}
          className={classes.switch}
          onToggle={() => updateDataLayer('sms')}
        />
      </div>

      <div className={clsx(className, classes.card)}>
        <Txt className={classes.label}>Email</Txt>

        <Switch
          isLoading={isMutationInFlight || !subscriptions}
          checked={subscriptionChannels?.surveyInviteContactChannels?.includes('email')}
          className={classes.switch}
          onToggle={() => updateDataLayer('email')}
        />
      </div>

      <div className={clsx(className, classes.card)}>
        <Txt className={classes.label}>Push Notifications</Txt>
        <Switch
          isLoading={isMutationInFlight || !subscriptions}
          checked={subscriptionChannels?.surveyInviteContactChannels?.includes('pushNotification')}
          className={classes.switch}
          onToggle={() => updateDataLayer('pushNotification')}
        />
      </div>
    </>
  );
}
